import React from 'react';
import { NextPageContext } from 'next';
import Head from 'next/head';
import ArticleHero from '../../components/articleHero';
import EventDate from '../../components/eventDate';
import EventTime from '../../components/eventTime';
import BannerAd from '../../components/ad/banner';
import SideAds, { BlockAdsOne, BlockAdsTwo } from '../../components/sideAds';
import Layout from '../../components/layout';
import Button from '../../components/button';
import { SEOFromQL } from '../../components/seo';
import { wpToCF } from '../../utils/data/cdn';
import pageWrapper from '../../containers/pageWrapper';
import FourOhFour from '../404';
import getTitleFromPage from '../../utils/data/getTitleFromPage';
import { fetchEventByURI } from '../../graphql/events';
import { contentWpToCF } from '../../utils/data/cdn';

interface Props {
  menus: WordpressHeaderMenu[];
  event: WordPressTechnicalSession;
  adverts: KeyedAdverts;
  error?: any;
}

const EventDetails = ({ event }: { event: WordPressTechnicalSession }) => {
  return (
    <div className='p-4 my-4 bg-abn-gray-light'>
      <p>
        <strong>Location: </strong> {event.eventDetails.eventLocation}
      </p>
      <p>
        <strong>Date: </strong>{' '}
        <EventDate
          startDate={event.eventDetails.eventStartDate}
          endDate={event.eventDetails.eventEndDate}
        />
      </p>

      <p>
        <strong>Time: </strong>{' '}
        {event.eventDetails.eventStartTime ? (
          <EventTime
            startTime={event.eventDetails.eventStartTime}
            endTime={event.eventDetails.eventEndTime}
          />
        ) : (
          'TBD'
        )}
      </p>
      <p>
        <strong>Cost: </strong>{' '}
        {event.eventDetails.eventCost
          ? `${event.eventDetails.eventCost}`
          : `TBD`}
      </p>
      {event.eventDetails.url && (
        <Button className='mt-4' href={event.eventDetails.url} openInNewTab>
          Buy now →
        </Button>
      )}
    </div>
  );
};

const SessionPartners = ({ event }: { event: WordPressTechnicalSession }) => {
  if (!event.eventSponsorship.banner?.mediaItemUrl) {
    return null;
  }
  return (
    <div className='flex items-center justify-center'>
      <img
        src={wpToCF(event.eventSponsorship.banner.mediaItemUrl)}
        alt='Event sponsors'
      />
    </div>
  );
};

const Event = ({ event, menus, adverts }: Props) => {
  const title = getTitleFromPage(event?.title);
  return !event ? (
    <FourOhFour />
  ) : (
    <>
      <Head>
        <title key='title'>{title}</title>
        <SEOFromQL
          ogImage={event.articleImage.thumbnail?.mediaItemUrl}
          seo={event.seo}
        />
      </Head>
      <Layout menus={menus}>
        <ArticleHero
          title={event.title}
          image={event.articleImage.thumbnail?.mediaItemUrl}
        />
        <BannerAd className='my-4' advert={adverts['banner-top']} />
        <div className='grid grid-cols-2 gap-0 mb-4 md:grid-cols-4 sm:gap-4'>
          <div className='col-span-2 my-4 md:col-span-3 md:my-0 article'>
            <article
              className='mb-4'
              dangerouslySetInnerHTML={{
                __html: contentWpToCF(event.content)
              }}
            />
            <EventDetails event={event} />
            <SessionPartners event={event} />
          </div>

          <SideAds adverts={adverts} />
        </div>
        <BlockAdsOne adverts={adverts} />
        <BlockAdsTwo adverts={adverts} />
      </Layout>
    </>
  );
};

Event.getInitialProps = async ({ query }: NextPageContext) => {
  const slug = Array.isArray(query.slug) ? query.slug[0] : query.slug;
  const { event } = await fetchEventByURI(slug);
  return { event };
};

export default pageWrapper(Event);
